import React, { useMemo } from 'react';
import { Box } from '@tanium/react-box';
import { Button } from '@tanium/react-button';
import styled from '@tanium/react-emotion';
import {
  BestPracticesIcon,
  DataSheetIcon,
  ReleaseNotesIcon,
  ReportCustomIcon,
} from '@tanium/react-graphics';
import { useHomePageSection } from '@tanium/react-home-page-section-context';
import { Stack, StackChild } from '@tanium/react-stack';
import { useTheme } from '@tanium/react-theme-context';
import { css } from '@tanium/style-system';
import { ThemeFontWeights } from '@tanium/theme-data';
import { getDataSheetForProduct } from './linkUrls/getDataSheetForProduct';
import { getReleaseNotesForProduct } from './linkUrls/getReleaseNotesForProduct';
import { getResourceCenterForProduct } from './linkUrls/getResourceCenterForProduct';
import { getUserGuideForProduct } from './linkUrls/getUserGuideForProduct';
import { ProductNameProp } from './linkUrls/types';
import { useTranslation } from './localization';
import {
  BannerIcon,
  ContainerBorder,
  ContainerFill,
  ContainerShadow,
  HeadingText,
  SummaryText,
} from './themeDefinitions';
import { WelcomeLink, WelcomeLinks } from './WelcomeLinks';

export interface WelcomeBannerProps {
  /**
   * The summary header. Should be a short tagline that explains what the
   * product is and how it can be used.
   */
  header: React.ReactNode;
  /**
   * The body of the summary. Should be a short paragraph about the product.
   */
  summary: React.ReactNode;
  /**
   * The name of your product. Standard links will automatically be derived from this.
   */
  productName: ProductNameProp['productName'];
  /**
   * Specify a URL to override the link to your product's page on the Tanium Resource Center site, or set to `false`
   * to hide this link from the WelcomeBanner.
   */
  resourceCenterLinkUrl?: string | false;
  /**
   * Specify a URL to override the link to your product's user guide, or set to `false`
   * to hide this link from the WelcomeBanner.
   */
  userGuideLinkUrl?: string | false;
  /**
   * Specify a URL to override the link to your product's release notes, or set to `false`
   * to hide this link from the WelcomeBanner.
   */
  releaseNotesLinkUrl?: string | false;
  /**
   * Specify a URL to override the link to your product's data sheet, or set to `false`
   * to hide this link from the WelcomeBanner.
   */
  dataSheetLinkUrl?: string | false;
  /**
   * An array of `WelcomeLink` to render the links in the banner.
   * Each object can contain a string of `text` an `href` and an 'icon'.
   */
  additionalLinks?: WelcomeLink[];
  unstable_resourceCenterLinkText?: string;
  unstable_userGuideLinkText?: string;
  unstable_releaseNotesLinkText?: string;
  unstable_dataSheetLinkText?: string;
  unstable_hideLabel?: string;
}

const Header = styled('h3')(
  css({
    fontSize: 18,
    fontWeight: ThemeFontWeights.Bold,
    lineHeight: '24px',
    margin: 0,
    color: HeadingText,
  }),
);

const Summary = styled('div')(
  css({
    fontSize: 16,
    lineHeight: '21px',
    color: SummaryText,
  }),
);

const getLink = (overrideUrl: string | undefined | false, generatedUrl: string) => {
  if (overrideUrl === false) {
    return false;
  }
  return overrideUrl ?? generatedUrl;
};

export const WelcomeBanner = ({
  header,
  summary,
  productName,
  resourceCenterLinkUrl: resourceCenterLinkUrlOverride,
  userGuideLinkUrl: userGuideLinkUrlOverride,
  releaseNotesLinkUrl: releaseNotesLinkUrlOverride,
  dataSheetLinkUrl: dataSheetLinkUrlOverride,
  additionalLinks,
  unstable_resourceCenterLinkText,
  unstable_userGuideLinkText,
  unstable_releaseNotesLinkText,
  unstable_dataSheetLinkText,
  unstable_hideLabel,
}: WelcomeBannerProps) => {
  const { t } = useTranslation();
  const { promptHideSection } = useHomePageSection();
  const isTaas = !!window.head_conf?.console?.is_taas;
  const theme = useTheme();

  const resourceCenterLinkText = unstable_resourceCenterLinkText ?? t('Resource Center');
  const userGuideLinkText = unstable_userGuideLinkText ?? t('User Guide');
  const releaseNotesLinkText = unstable_releaseNotesLinkText ?? t('Release Notes');
  const dataSheetLinkText = unstable_dataSheetLinkText ?? t('Data Sheet');
  const hideLabel = unstable_hideLabel ?? t('Hide');

  const generatedResourceCenterLinkUrl = getResourceCenterForProduct({ productName });
  const resourceCenterLinkUrl = getLink(
    resourceCenterLinkUrlOverride,
    generatedResourceCenterLinkUrl,
  );

  const generatedUserGuideLinkUrl = getUserGuideForProduct({ productName });
  const userGuideLinkUrl = getLink(userGuideLinkUrlOverride, generatedUserGuideLinkUrl);

  const generatedReleaseNotesLinkUrl = getReleaseNotesForProduct({ productName });
  const releaseNotesLinkUrl = getLink(releaseNotesLinkUrlOverride, generatedReleaseNotesLinkUrl);

  const generatedDataSheetLinkUrl = getDataSheetForProduct({ productName });
  const dataSheetLinkUrl = getLink(dataSheetLinkUrlOverride, generatedDataSheetLinkUrl);

  const links = useMemo(
    () =>
      [
        resourceCenterLinkUrl && {
          text: resourceCenterLinkText,
          href: resourceCenterLinkUrl,
          icon: ReportCustomIcon,
        },
        userGuideLinkUrl && {
          text: userGuideLinkText,
          href: userGuideLinkUrl,
          icon: BestPracticesIcon,
        },
        releaseNotesLinkUrl &&
          !isTaas && {
            text: releaseNotesLinkText,
            href: releaseNotesLinkUrl,
            icon: ReleaseNotesIcon,
          },
        dataSheetLinkUrl && {
          text: dataSheetLinkText,
          href: dataSheetLinkUrl,
          icon: DataSheetIcon,
        },
        ...(additionalLinks || []),
      ].filter(Boolean) as WelcomeLink[],
    [
      additionalLinks,
      resourceCenterLinkUrl,
      dataSheetLinkUrl,
      isTaas,
      releaseNotesLinkUrl,
      resourceCenterLinkText,
      dataSheetLinkText,
      releaseNotesLinkText,
      userGuideLinkText,
      userGuideLinkUrl,
    ],
  );

  return (
    <Stack
      minHeight="130px"
      itemSpacing={1}
      backgroundColor={ContainerFill}
      border={1}
      borderRadius={1}
      p={1}
      borderColor={ContainerBorder}
      boxShadow={ContainerShadow}
    >
      <StackChild grow={0} basis="225px" shrink={0} alignSelf="center">
        <Box px={1}>
          <Box as={BannerIcon(theme)} display="block" width="170px" height="115px" />
        </Box>
      </StackChild>
      <StackChild grow={1}>
        <Stack py={2} direction="column" itemSpacing={2}>
          <Stack direction="column">
            <Header>{header}</Header>
            <Summary>{summary}</Summary>
          </Stack>
          {!!links.length && <WelcomeLinks links={links} />}
        </Stack>
      </StackChild>
      {promptHideSection && (
        <Button type="button" size="sm" variant="secondary" onClick={promptHideSection}>
          {hideLabel}
        </Button>
      )}
    </Stack>
  );
};
